import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowsClockwise,
  Trash,
  PencilSimple,
  Sparkle,
  PlayCircle,
  SpeakerHigh,
  Image as ImageIcon,
  ArrowUpRight,
  Star,
  ThumbsUp,
  ThumbsDown,
  Lightbulb,
  ClockClockwise,
  PauseCircle,
  PlayCircle as PlayCircleIcon,
  Share,
  Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { TopBar } from "../components/TopBar";
import { SketchUpload } from "../components/SketchUpload";
import { ReferenceDrawer } from "../components/ReferenceDrawer";
import {
  getRecce,
  rescout,
  deleteRecce,
  toggleStar,
  deleteReference,
  markSeen,
  setReferenceFeedback,
  getMyCredits,
  enableShare,
  disableShare,
  togglePause,
} from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOUT_STEPS = [
  "Reading the idea for mood, subject, and reference cues…",
  "Sweeping photography and film archives…",
  "Checking recent ad and editorial work…",
  "Hunting for clips on YouTube and Vimeo…",
  "Scouting mood tracks on SoundCloud & YouTube…",
  "Filtering for visual coherence…",
  "Assembling the board…",
];

// Kinds that count as "primary" cards for the 10-card limit.
const CARD_KINDS = ["image", "video", "audio", "imagined", "memory", "sketch", "web"];

// Rank for card ordering within the 10 — visuals + clips first.
const kindRank = (k) =>
  ({ sketch: 0, imagined: 1, image: 2, video: 3, audio: 4, memory: 5, web: 6 }[k] ?? 9);

export default function RecceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(null);
  const [credits, setCredits] = useState(null);
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  const statusRef = useRef(null);
  // Capture the recce's last_opened_at ONCE on first load so NEW badges
  // persist for the whole session (markSeen bumps last_opened_at to "now"
  // and subsequent refreshes would otherwise erase the badge).
  const openedAtRef = useRef(null);

  const refresh = async () => {
    try {
      const d = await getRecce(id);
      setData(d);
      setError(null);
      statusRef.current = d?.recce?.status || null;
      // Freeze last_opened_at on the first successful load so NEW badges stay.
      if (openedAtRef.current === null) {
        openedAtRef.current = d?.recce?.last_opened_at || "";
      }
    } catch (e) {
      // Only surface a persistent error when we have no data at all AND the status is "not found".
      // A single transient 401/network hiccup during cookie propagation shouldn't black-hole the page.
      if (e?.response?.status === 404) {
        setError("Recce not found.");
      } else if (!data) {
        setError("Could not load recce.");
      }
      // Otherwise: keep whatever data we have on screen; the next tick will retry.
    }
  };

  useEffect(() => {
    refresh();
    markSeen(id).catch(() => {});
    getMyCredits().then(setCredits).catch(() => {});
  }, [id]);

  const [sparkedRef, setSparkedRef] = useState(null);
  const sparkedRefRef = useRef(null);
  useEffect(() => {
    sparkedRefRef.current = sparkedRef;
  }, [sparkedRef]);

  // Poll while scouting — uses a ref so Safari/Mac doesn't lose the loop on stale closures.
  // Also re-fires on tab focus so a user returning to the tab sees the moodboard immediately.
  // Self-heals from transient fetch errors: if we don't yet have data, keep retrying.
  // Keeps polling while a spark is pending so the banner clears as soon as new refs arrive.
  useEffect(() => {
    let cancelled = false;
    const shouldPoll = () =>
      statusRef.current === "scouting"
      || !statusRef.current
      || Boolean(sparkedRefRef.current);
    const tick = async () => {
      if (cancelled) return;
      if (shouldPoll()) {
        await refresh();
      }
    };
    const t = setInterval(tick, 1500);
    const onFocus = () => {
      if (shouldPoll()) refresh();
    };
    const onVisible = () => {
      if (!document.hidden && shouldPoll()) refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [id]);

  const recce = data?.recce;
  const refs = data?.references || [];

  // Build the 10-card board: starred always in; then interleave by kind so the
  // moodboard has a GUARANTEED mix of visuals + clips + tracks (per spec).
  // References whose image URL has 404'd/hotlink-blocked (tracked in brokenIds)
  // are filtered out so a backup ref gets promoted into their slot.
  // Also filter out refs that would render as a BLANK placeholder — a memory
  // or image/video card with neither asset_id nor image_url has no visual at all.
  // 👎-marked refs are hidden too (and persist hidden across refreshes).
  const cards = useMemo(() => {
    const hasVisual = (r) => {
      if (brokenIds.has(r.id)) return false;
      if (r.feedback === "down") return false; // user told us to skip this one
      if (r.asset_id) return true;
      if (r.image_url) return true;
      if (r.kind === "audio") return true;
      if (r.kind === "web") return true;
      return false;
    };
    const eligible = refs.filter(
      (r) => CARD_KINDS.includes(r.kind) && hasVisual(r),
    );
    const buckets = {
      sketch: [],
      imagined: [],
      image: [],
      video: [],
      audio: [],
      memory: [],
      web: [],
    };
    const starred = [];
    for (const r of eligible) {
      if (r.starred) starred.push(r);
      else if (buckets[r.kind]) buckets[r.kind].push(r);
    }
    // Sort images/memory by score, keep others in natural order
    buckets.image.sort((a, b) => (b.score || 0) - (a.score || 0));

    // Target distribution across the 10 cards (sketch first, then interleaved).
    const picks = [...starred.slice(0, 10)];
    const draw = (kind, n) => {
      for (let i = 0; i < n && buckets[kind].length && picks.length < 10; i++) {
        picks.push(buckets[kind].shift());
      }
    };
    draw("sketch", 1);
    draw("imagined", 2);
    draw("image", 3);
    draw("video", 2);
    draw("audio", 1);
    draw("memory", 1);
    // Fill remaining slots greedily by order sketch > imagined > image > video > audio > memory > web
    for (const k of ["imagined", "image", "video", "audio", "memory", "web"]) {
      draw(k, 10);
      if (picks.length >= 10) break;
    }
    return picks.slice(0, 10);
  }, [refs, brokenIds]);

  const handleBrokenImage = (refId) => {
    setBrokenIds((prev) => {
      if (prev.has(refId)) return prev;
      const next = new Set(prev);
      next.add(refId);
      return next;
    });
  };

  const handleStar = async (r) => {
    try {
      const updated = await toggleStar(r.id);
      setData((d) => ({
        ...d,
        references: d.references.map((x) => (x.id === r.id ? updated : x)),
      }));
      if (active && active.id === r.id) setActive(updated);
    } catch (e) {
      toast.error("Couldn't update star", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
    }
  };

  const handleFeedback = async (r, nextFeedback) => {
    const prev = r.feedback || "";
    // Toggling the same signal clears it (so a second tap on 👍 removes it).
    const value = prev === nextFeedback ? "" : nextFeedback;

    // Optimistic UI — update local state BEFORE the network round-trip so
    // 👍 instantly stars, 👎 instantly removes the card, 💡 instantly shows the banner.
    setData((d) =>
      d
        ? {
            ...d,
            references: d.references.map((x) =>
              x.id === r.id ? { ...x, feedback: value } : x,
            ),
          }
        : d,
    );
    if (active && active.id === r.id) {
      setActive((a) => (a ? { ...a, feedback: value } : a));
    }

    // 👍 → auto-star the ref so it's visibly kept + ranked up in future renders.
    if (value === "up" && !r.starred) {
      try {
        await toggleStar(r.id);
        setData((d) =>
          d
            ? {
                ...d,
                references: d.references.map((x) =>
                  x.id === r.id ? { ...x, feedback: value, starred: true } : x,
                ),
              }
            : d,
        );
      } catch {
        /* non-fatal */
      }
    }

    try {
      await setReferenceFeedback(r.id, value);
    } catch (e) {
      // Roll back on failure
      setData((d) =>
        d
          ? {
              ...d,
              references: d.references.map((x) =>
                x.id === r.id ? { ...x, feedback: prev } : x,
              ),
            }
          : d,
      );
      toast.error("Couldn't save feedback", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
      return;
    }

    if (value === "down") {
      toast("Skipped. Pulling in a backup reference.", { duration: 2000 });
    } else if (value === "up") {
      toast("Saved to this board.", { duration: 1800 });
    } else if (value === "spark") {
      setSparkedRef({ id: r.id, title: r.title, at: Date.now() });
      toast("Scouting deeper on this reference…", {
        description: "New finds will land on the board shortly.",
      });
      // Give the backend a moment to flip status→scouting, then start polling.
      setTimeout(refresh, 1200);
    }
  };

  // When the recce's updated_at advances OR status returns to ready AFTER a spark,
  // we can safely dismiss the banner — new refs have landed on the board.
  useEffect(() => {
    if (!sparkedRef) return;
    if (!data?.recce) return;
    const updatedAt = data.recce.updated_at
      ? new Date(data.recce.updated_at).getTime()
      : 0;
    const newRefArrived = data.references.some(
      (r) => new Date(r.created_at || 0).getTime() > sparkedRef.at,
    );
    if (newRefArrived && data.recce.status !== "scouting") {
      setSparkedRef(null);
    }
  }, [data, sparkedRef]);

  const handleDelete = async (r) => {
    try {
      await deleteReference(r.id);
      setData((d) => ({ ...d, references: d.references.filter((x) => x.id !== r.id) }));
    } catch (e) {
      toast.error("Couldn't discard this reference", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
    }
  };

  const handleRescout = async () => {
    if (!recce) return;
    if (!window.confirm("Dispatch Recce again on this idea?")) return;
    try {
      await rescout(recce.id);
      toast("Agent dispatched", { description: "Scouting fresh references…" });
      refresh();
    } catch (e) {
      toast.error("Could not re-scout", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
    }
  };

  const handleTogglePause = async () => {
    if (!recce) return;
    const wasPaused = !!recce.paused;
    try {
      await togglePause(recce.id);
      toast(wasPaused ? "Scouting resumed" : "Scouting paused", {
        description: wasPaused
          ? "I'll keep digging in the background."
          : "I'll stop looking until you resume.",
      });
      refresh();
    } catch (e) {
      toast.error("Could not update pause state", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
    }
  };

  const handleDeleteRecce = async () => {
    if (!recce) return;
    if (!window.confirm("Delete this recce permanently?")) return;
    await deleteRecce(recce.id);
    navigate("/");
  };

  if (error && !data) {
    return (
      <div className="min-h-screen bg-bg">
        <TopBar />
        <main className="max-w-[920px] mx-auto px-6 pt-20 text-center">
          <p className="text-[13px] text-ink-dim">{error}</p>
          <Link
            to="/"
            className="font-serif text-2xl mt-4 inline-block text-amber hover:underline"
          >
            ← Back to intake
          </Link>
        </main>
      </div>
    );
  }

  if (!recce) {
    return (
      <div className="min-h-screen bg-bg">
        <TopBar />
        <main className="max-w-[920px] mx-auto px-6 pt-32 text-center text-ink-dim text-sm">
          loading…
        </main>
      </div>
    );
  }

  const isScouting = recce.status === "scouting";
  const titleStr = recce.title || recce.brief || "Untitled";

  return (
    <div className="min-h-screen bg-bg">
      <TopBar />

      <main className="max-w-[920px] mx-auto px-6 pb-20" data-testid="recce-detail-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 text-[12px] text-ink-dim hover:text-amber"
          data-testid="back-to-intake"
        >
          <ArrowLeft size={14} /> all boards
        </Link>

        {isScouting && cards.length === 0 ? (
          <ScoutingState />
        ) : (
          <>
            <BoardHead
              recce={recce}
              refs={refs}
              onRescout={handleRescout}
              onDelete={handleDeleteRecce}
              onTogglePause={handleTogglePause}
            />

            <ScheduleStrip recce={recce} credits={credits} />

            {sparkedRef && (
              <section
                className="mt-5 border border-amber-dim rounded-xl px-4 py-3 flex items-center gap-3 bg-amber/10"
                data-testid="spark-banner"
              >
                <Lightbulb size={18} className="text-amber animate-pulse" weight="fill" />
                <div className="flex-1 text-[13px] text-ink min-w-0">
                  <span className="font-medium">Digging deeper on</span>{" "}
                  <span className="italic text-ink-dim truncate">
                    "{(sparkedRef.title || "this reference").slice(0, 60)}"
                  </span>
                  <span className="text-ink-dim"> — new finds land on this board shortly.</span>
                </div>
              </section>
            )}

            {/* Sketch upload sits above the grid as a small accordion-style card */}
            <div className="mt-6">
              <SketchUpload
                recce={recce}
                onUpdated={(updated) =>
                  setData((d) => (d ? { ...d, recce: updated } : d))
                }
              />
            </div>

            {cards.length === 0 ? (
              <div className="mt-10 border border-dashed border-line rounded-xl p-10 text-center text-[13px] text-ink-dim">
                No references on the board yet. The agent is working…
              </div>
            ) : (
              <div
                className="mt-8"
                style={{ columnCount: 3, columnGap: 14 }}
                data-testid="moodboard-grid"
              >
                <style>{`
                  @media (max-width: 760px) { [data-testid="moodboard-grid"] { column-count: 2 !important; } }
                  @media (max-width: 480px) { [data-testid="moodboard-grid"] { column-count: 1 !important; } }
                `}</style>
                {cards.map((r, i) => (
                  <Card
                    key={r.id}
                    ref_={r}
                    index={i}
                    onOpen={setActive}
                    onStar={handleStar}
                    onFeedback={handleFeedback}
                    onBroken={handleBrokenImage}
                    openedAt={openedAtRef.current}
                  />
                ))}
              </div>
            )}

            <BoardFoot />
          </>
        )}

        <ReferenceDrawer
          ref_={active}
          recceId={recce?.id}
          shareToken={recce?.share_token || ""}
          onShareTokenMinted={(tok) =>
            setData((d) =>
              d ? { ...d, recce: { ...d.recce, share_token: tok } } : d,
            )
          }
          onClose={() => setActive(null)}
          onStar={handleStar}
          onDelete={handleDelete}
        />
      </main>
    </div>
  );
}

const BoardHead = ({ recce, refs, onRescout, onDelete, onTogglePause }) => {
  const isScouting = recce.status === "scouting";
  const isPaused = !!recce.paused;
  return (
    <section className="pt-11 flex items-start justify-between gap-5 flex-wrap">
      <div className="min-w-0 flex-1">
        <h2 className="font-serif font-medium text-[24px] leading-snug max-w-[36ch]">
          “{recce.title || recce.brief}”
        </h2>
        <p className="text-[13px] text-ink-dim mt-1.5" data-testid="board-meta">
          {refs.filter((r) => CARD_KINDS.includes(r.kind)).length} references found · last
          scouted {fmtAgo(recce.updated_at)}
          {recce.ai_generated && " · AI images on"}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <StatusPill scouting={isScouting} paused={isPaused} />
        <ShareButton recce={recce} />
        <button
          type="button"
          onClick={onTogglePause}
          data-testid="pause-toggle-btn"
          className={`inline-flex items-center gap-1.5 text-[12.5px] rounded-full px-3.5 py-1.5 border transition-colors ${
            isPaused
              ? "bg-amber text-[#20170a] border-amber hover:bg-amber-hover"
              : "text-ink border-line hover:border-amber-dim"
          }`}
          title={isPaused ? "Resume scouting" : "Pause scouting (saves credits)"}
        >
          {isPaused ? <PlayCircleIcon size={13} weight="fill" /> : <PauseCircle size={13} />}
          {isPaused ? "resume" : "pause"}
        </button>
        <button
          type="button"
          onClick={onRescout}
          disabled={isScouting || isPaused}
          data-testid="rescout-btn"
          className="inline-flex items-center gap-1.5 text-[12.5px] text-ink border border-line rounded-full px-3.5 py-1.5 hover:border-amber-dim disabled:opacity-40"
          title={isPaused ? "Resume first to scout again" : "dispatch Recce again"}
        >
          <ArrowsClockwise size={13} />
          scout again
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid="delete-recce-btn"
          className="p-2 text-ink-dim hover:text-red-500"
          aria-label="delete recce"
        >
          <Trash size={14} />
        </button>
      </div>
    </section>
  );
};

const ShareButton = ({ recce }) => {
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(recce.share_token || "");
  const [justCopied, setJustCopied] = useState(false);

  // Keep local token in sync when the parent re-fetches.
  useEffect(() => {
    setToken(recce.share_token || "");
  }, [recce.share_token]);

  const buildUrl = (t) =>
    `${window.location.origin}/share/${t}`;

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let t = token;
      if (!t) {
        const resp = await enableShare(recce.id);
        t = resp.share_token;
        setToken(t);
      }
      const url = buildUrl(t);
      try {
        await navigator.clipboard.writeText(url);
        setJustCopied(true);
        setTimeout(() => setJustCopied(false), 2200);
        toast("Share link copied", {
          description: "Anyone with the link can view this board — no sign-in needed.",
        });
      } catch {
        // Fallback: show URL in a toast so the user can copy manually.
        toast("Share link ready", { description: url });
      }
    } catch (e) {
      toast.error("Couldn't create a share link", {
        description: e?.response?.data?.detail || "Try again in a moment.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid="share-btn"
      className="inline-flex items-center gap-1.5 text-[12.5px] text-ink border border-line rounded-full px-3.5 py-1.5 hover:border-amber-dim disabled:opacity-40"
      title="Copy a public link to this board"
    >
      {justCopied ? <Check size={13} /> : <Share size={13} />}
      {justCopied ? "copied" : token ? "copy link" : "share"}
    </button>
  );
};

const ScheduleStrip = ({ recce, credits }) => {
  const paused = !!recce.paused;
  const nextAt = recce.next_scout_at ? new Date(recce.next_scout_at) : null;
  const passesToday = Number(recce.passes_today || 0);
  const dailyBudget = Number(credits?.scouts_per_day_free || 3);
  const planLabel = credits?.plan || "free";
  const quotaExhausted = passesToday >= dailyBudget;

  if (paused) {
    const isManual = recce.pause_reason === "manual";
    return (
      <section
        className="mt-5 border border-line rounded-xl px-4 py-3 flex items-center gap-3 bg-surface"
        data-testid="schedule-strip-paused"
      >
        <PauseCircle size={18} className="text-ink-dim" />
        <div className="flex-1 text-[13px] text-ink">
          {isManual ? (
            <>
              <span className="font-medium">Paused by you.</span>{" "}
              <span className="text-ink-dim">
                Tap <em>resume</em> above to start scouting again.
              </span>
            </>
          ) : (
            <>
              <span className="font-medium">Paused — no activity for 48 hours.</span>{" "}
              <span className="text-ink-dim">
                Opening this board resumed it. Scouting picks up now.
              </span>
            </>
          )}
        </div>
      </section>
    );
  }

  const nextLine =
    nextAt && !isNaN(nextAt.getTime())
      ? fmtLocalTime(nextAt)
      : quotaExhausted
      ? "resuming at local midnight"
      : "soon";

  return (
    <section
      className="mt-5 border border-line rounded-xl px-4 py-3 flex items-center gap-3 bg-surface flex-wrap"
      data-testid="schedule-strip"
    >
      <ClockClockwise size={18} className="text-amber" />
      <div className="flex-1 text-[13px] text-ink min-w-0">
        {quotaExhausted ? (
          <>
            <span className="font-medium">Done for today.</span>{" "}
            <span className="text-ink-dim">
              Next scouts resume at midnight · {passesToday} of {dailyBudget} used.
            </span>
          </>
        ) : (
          <>
            <span className="font-medium">Next scout: {nextLine}</span>
            <span className="text-ink-dim"> · {passesToday} of {dailyBudget} used today.</span>
          </>
        )}
      </div>
      <span
        className="text-[11px] uppercase tracking-[0.14em] text-amber bg-amber/10 border border-amber-dim rounded-full px-2.5 py-1"
        data-testid="always-on-badge"
      >
        always-on · {planLabel}
      </span>
    </section>
  );
};

function fmtLocalTime(d) {
  try {
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = d.toDateString() === tomorrow.toDateString();
    const time = d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    if (sameDay) return time;
    if (isTomorrow) return `${time} tomorrow`;
    return `${time}, ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  } catch {
    return "soon";
  }
}

const StatusPill = ({ scouting, paused }) => (
  <span
    className="inline-flex items-center gap-[7px] text-[12.5px] text-ink-dim border border-line rounded-full px-3 py-1.5 whitespace-nowrap"
    data-testid="status-pill"
  >
    {paused ? (
      <>
        <span className="w-[6px] h-[6px] rounded-full bg-ink-dim" />
        Paused
      </>
    ) : scouting ? (
      <>
        <span className="w-[6px] h-[6px] rounded-full bg-amber animate-lantern-pulse" />
        Still scouting in the background
      </>
    ) : (
      <>
        <span className="w-[6px] h-[6px] rounded-full bg-emerald-500" />
        Ready
      </>
    )}
  </span>
);

const ScoutingState = () => (
  <section className="pt-28 text-center" id="scouting" data-testid="scouting-state">
    <div
      className="w-16 h-16 mx-auto mb-7 rounded-full animate-lantern-pulse"
      style={{
        background:
          "radial-gradient(circle at 35% 30%, #f0c88a, #b5651d 60%, #8a4d16 100%)",
        boxShadow: "0 0 36px 6px rgba(181,101,29,.28)",
      }}
    />
    <h2 className="font-serif font-medium text-[22px] mb-2">Out scouting.</h2>
    <p className="text-ink-dim text-[14.5px]">
      Recce is combing film stills, photography, campaigns, clips &amp; mood tracks for your idea.
    </p>
    <div className="mt-9 max-w-[380px] mx-auto text-left text-[13px] text-ink-ghost flex flex-col gap-2.5">
      {SCOUT_STEPS.map((s, i) => (
        <div
          key={s}
          className="opacity-0 translate-y-1 animate-line-reveal"
          style={{ animationDelay: `${i * 0.45}s` }}
        >
          <span className="text-ink-dim">→</span> {s}
        </div>
      ))}
    </div>
  </section>
);

const BoardFoot = () => (
  <div className="mt-10 border-t border-line pt-6 flex items-center justify-between gap-4 flex-wrap">
    <p className="text-[13px] text-ink-dim max-w-[38ch] leading-[1.5]">
      On the free plan, Recce scouts when you ask. Go always-on and it keeps circling this
      idea overnight, adding to the board while you sleep.
    </p>
    <button
      type="button"
      data-testid="upgrade-btn"
      onClick={(e) => {
        const btn = e.currentTarget;
        const original = btn.textContent;
        btn.textContent = "Always-on is the paid plan — coming soon";
        setTimeout(() => {
          btn.textContent = original;
        }, 2200);
      }}
      className="bg-transparent text-ink border border-line rounded-full px-5 py-2.5 text-[13.5px] font-medium hover:border-amber-dim"
    >
      See always-on plan
    </button>
  </div>
);

// -------- Reference card (one card type, varies by kind) --------

const refImageSrc = (r) => {
  if (r.asset_id) return `${API}/assets/${r.asset_id}`;
  return r.image_url || "";
};

const kindLabel = (r) => {
  if (r.kind === "image") return "Image";
  if (r.kind === "video") return "Video clip";
  if (r.kind === "audio") return "Mood track";
  if (r.kind === "memory") return "From memory";
  if (r.kind === "imagined") return `AI-generated · ${r.generator || "agent"}`;
  if (r.kind === "sketch") return "Your sketch";
  return "Article";
};

const KindIcon = ({ kind }) => {
  const common = { size: 12, weight: "regular" };
  if (kind === "video") return <PlayCircle {...common} />;
  if (kind === "audio") return <SpeakerHigh {...common} />;
  if (kind === "imagined") return <Sparkle {...common} weight="fill" />;
  if (kind === "sketch") return <PencilSimple {...common} />;
  if (kind === "image" || kind === "memory") return <ImageIcon {...common} />;
  return null;
};

const Card = ({ ref_, index, onOpen, onStar, onFeedback, onBroken, openedAt }) => {
  const img = refImageSrc(ref_);
  const isTextOnly = ref_.kind === "web" && !img;
  const isImagined = ref_.kind === "imagined";
  const isSketch = ref_.kind === "sketch";
  const fb = ref_.feedback || "";
  // Memory refs with no image_url are fine (we render a kind-icon placeholder);
  // only treat ACTUAL image load failures (where we expected an image) as broken.
  const hasImage = Boolean(img) && !isTextOnly;
  // NEW badge: ref was added by an always-on pass since the user last opened the
  // board. `openedAt` is captured once per session so the badge survives markSeen.
  const isNew = isRefNew(ref_, openedAt);

  return (
    <article
      className={`relative break-inside-avoid mb-[14px] rounded-[14px] overflow-hidden bg-surface border opacity-0 translate-y-2.5 animate-card-in cursor-pointer group ${
        isSketch ? "border-ink shadow-[4px_4px_0_0_rgba(181,101,29,0.85)]" : "border-line"
      }`}
      style={{ animationDelay: `${index * 0.06}s` }}
      onClick={() => onOpen(ref_)}
      data-testid={`ref-${ref_.kind}-${ref_.id}`}
    >
      {isImagined && (
        <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 bg-bg/95 border border-line text-[9.5px] uppercase tracking-[0.18em] text-amber rounded-full px-2 py-1">
          <Sparkle size={9} weight="fill" />
          AI-generated
        </span>
      )}
      {isSketch && (
        <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-ink text-bg text-[9.5px] uppercase tracking-[0.22em] rounded-full px-2 py-1">
          <PencilSimple size={9} weight="bold" />
          your sketch
        </span>
      )}
      {isNew && !isSketch && (
        <span
          className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-amber text-[#20170a] text-[9.5px] font-mono uppercase tracking-[0.18em] rounded-full px-2 py-1 shadow-[0_2px_8px_rgba(181,101,29,.35)]"
          data-testid={`new-badge-${ref_.id}`}
        >
          <Sparkle size={9} weight="fill" /> new
        </span>
      )}

      {img && !isTextOnly ? (
        <div className="relative">
          <img
            src={img}
            alt={ref_.title}
            loading="lazy"
            className="w-full block"
            style={{ maxHeight: 320, objectFit: "cover" }}
            onError={() => {
              // Swap this card out for a backup reference the agent already found.
              // (asset_id images from our own storage never 404; this fires only on
              // external Tavily hits that hotlink-block or go dead.)
              if (hasImage && !ref_.asset_id && onBroken) {
                onBroken(ref_.id);
              }
            }}
          />
          {ref_.kind === "video" && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="bg-ink/80 rounded-full p-3">
                <PlayCircle size={22} weight="fill" className="text-bg" />
              </span>
            </span>
          )}
        </div>
      ) : ref_.kind === "audio" ? (
        <div className="w-full h-[150px] bg-surface-hi flex items-center justify-center text-amber/70">
          <SpeakerHigh size={32} weight="thin" />
        </div>
      ) : (
        // Web/article card without a thumbnail — give it a text-first "spine"
        // treatment instead of a blank hero area. Hostname bar + big quoted title.
        <div className="w-full px-[13px] pt-4 pb-3 bg-surface-hi border-b border-line">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-amber truncate">
              {hostname(ref_.url) || ref_.source || "article"}
            </span>
            <ArrowUpRight size={11} className="text-amber shrink-0" />
          </div>
          <p className="font-serif text-[15px] leading-[1.25] text-ink line-clamp-4">
            “{ref_.title || ref_.snippet || "Untitled"}”
          </p>
        </div>
      )}

      <div className="px-[13px] pt-3 pb-[14px]">
        <p className="text-[11px] font-semibold text-amber mb-1 flex items-center gap-1.5">
          <KindIcon kind={ref_.kind} />
          {kindLabel(ref_)}
        </p>
        {/* For text-only web cards, skip the duplicate title line — the hero already shows it. */}
        {!(ref_.kind === "web" && !img) && (
          <p className="text-[13px] text-ink leading-[1.4] mb-1 line-clamp-2">
            {ref_.title || ref_.snippet || "Untitled"}
          </p>
        )}
        {ref_.url && (
          <a
            href={ref_.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[11.5px] text-ink-ghost hover:text-amber inline-flex items-center gap-1 group/link"
            data-testid={`source-link-${ref_.id}`}
          >
            {ref_.source || hostname(ref_.url) || "open source"}
            <ArrowUpRight
              size={10}
              className="transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
            />
          </a>
        )}
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onStar(ref_);
        }}
        className="absolute bottom-2 right-2 p-1 text-ink-ghost hover:text-amber opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`star-btn-${ref_.id}`}
        aria-label="star reference"
      >
        <Star size={13} weight={ref_.starred ? "fill" : "regular"} />
      </button>

      {onFeedback && !isSketch && (
        <div
          className="absolute bottom-2 left-2 flex items-center gap-0.5 bg-bg/92 backdrop-blur-sm rounded-full px-1 py-0.5 border border-line opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
          data-testid={`feedback-row-${ref_.id}`}
        >
          <button
            type="button"
            onClick={() => onFeedback(ref_, "up")}
            aria-label="helpful reference"
            data-testid={`fb-up-${ref_.id}`}
            className={`p-1.5 rounded-full transition-colors ${
              fb === "up" ? "text-emerald-600" : "text-ink-ghost hover:text-emerald-600"
            }`}
            title="Helpful"
          >
            <ThumbsUp size={12} weight={fb === "up" ? "fill" : "regular"} />
          </button>
          <button
            type="button"
            onClick={() => onFeedback(ref_, "down")}
            aria-label="not useful"
            data-testid={`fb-down-${ref_.id}`}
            className={`p-1.5 rounded-full transition-colors ${
              fb === "down" ? "text-red-500" : "text-ink-ghost hover:text-red-500"
            }`}
            title="Not useful"
          >
            <ThumbsDown size={12} weight={fb === "down" ? "fill" : "regular"} />
          </button>
          <button
            type="button"
            onClick={() => onFeedback(ref_, "spark")}
            aria-label="scout deeper on this"
            data-testid={`fb-spark-${ref_.id}`}
            className={`p-1.5 rounded-full transition-colors ${
              fb === "spark" ? "text-amber" : "text-ink-ghost hover:text-amber"
            }`}
            title="Spark — scout deeper in this direction"
          >
            <Lightbulb size={12} weight={fb === "spark" ? "fill" : "regular"} />
          </button>
        </div>
      )}
    </article>
  );
};

function fmtAgo(iso) {
  if (!iso) return "just now";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function hostname(u) {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function isRefNew(ref_, openedAt) {
  // A ref is "new" if it landed on the board after the last time this user opened
  // the recce — this is how we surface what always-on passes brought overnight.
  // Sketches and the user's own uploads are never flagged.
  if (!ref_?.created_at) return false;
  if (ref_.kind === "sketch") return false;
  if (!openedAt) return true; // first-ever open shows every ref as new
  try {
    return new Date(ref_.created_at).getTime() > new Date(openedAt).getTime();
  } catch {
    return false;
  }
}
